import { COUNTRY_CODES } from "./countries";
import { configuracionDePais } from "../utils/configuracionDePais";

export const DOCUMENT_TYPES = {
  [COUNTRY_CODES.ECUADOR]: [
    { value: "Invoice", label: "Factura", sign: 1 },
    { value: "Credit Memo", label: "Notas de crédito", sign: -1 },
  ],
  [COUNTRY_CODES.PARAGUAY]: [
    { value: "Invoice", label: "Factura", sign: 1 },
    { value: "Credit Memo", label: "Notas de crédito", sign: -1 },
  ],
  [COUNTRY_CODES.HONDURAS]: [
    { value: "Invoice", label: "Factura", sign: 1 },
    { value: "Credit Memo", label: "Notas de crédito", sign: -1 },
  ],
  [COUNTRY_CODES.ELSALVADOR]: [
    { value: "Invoice", label: "Factura", sign: 1 },
    { value: "Credit Memo", label: "Notas de crédito", sign: -1 },
  ],
  // Sin factura electrónica ni boleta: los dos de Business Central y nada más.
  [COUNTRY_CODES.PUERTORICO]: [
    { value: "Invoice", label: "Factura", sign: 1 },
    { value: "Credit Memo", label: "Notas de crédito", sign: -1 },
  ],
  [COUNTRY_CODES.PERU]: [
    { value: "Invoice", label: "Factura", sign: 1 },
    { value: "Boleta", label: "Boleta de Venta", sign: 1 },
    { value: "Credit Memo", label: "Notas de crédito", sign: -1 },
  ],
};

export const getDocumentTypes = (countryCode) => {
  return configuracionDePais(DOCUMENT_TYPES, countryCode, "tipos de documento");
};

/**
 * Returns the sign (+1 or -1) for a given document type value.
 * Falls back to checking if the type matches any known negative-sign type across all countries.
 * Defaults to +1 if not found.
 */
export const getDocumentTypeSign = (documentType, countryCode) => {
  const types = getDocumentTypes(countryCode);
  const found = types.find((t) => t.value === documentType);
  if (found) return found.sign;
  // Fallback: check all countries for the document type
  for (const countryTypes of Object.values(DOCUMENT_TYPES)) {
    const match = countryTypes.find((t) => t.value === documentType);
    if (match) return match.sign;
  }
  return 1; // default positive
};

export const matchesDocumentType = (actualType, expectedType, countryCode) => {
  if (expectedType === "Invoice") {
    return (
      actualType === "Invoice" || (countryCode === COUNTRY_CODES.PERU && actualType === "Boleta")
    );
  }
  return actualType === expectedType;
};

/**
 * La única lista de literales que el terminal traduce a `Document_Type`.
 *
 * Ese campo lo declara Business Central, en todos los países, con dos valores:
 * `Invoice` y `Credit Memo`. La boleta de Perú es una factura para BC --lo que
 * la separa de una factura es el `SUNAT_Document` ("03" contra "01"), que viaja
 * en su propio campo--, así que su `Document_Type` sale como `Invoice`, igual
 * que el de cualquier otro país.
 *
 * Devuelve `null` si el texto no es ninguno de los tres: **no adivina**. Quien
 * llame decide qué hacer con eso, porque no todos los sitios pueden hacer lo
 * mismo --el envío corta, y una consulta que tiene que contestar siempre, no--.
 */
const TIPO_DE_BC_POR_LITERAL = {
  Invoice: "Invoice",
  Boleta: "Invoice",
  "Credit Memo": "Credit Memo",
};

export const tipoDeDocumentoParaBC = (documentType) =>
  TIPO_DE_BC_POR_LITERAL[String(documentType ?? "").trim()] ?? null;

/**
 * El `Document_Type` del envío a Business Central (`insertaFacturasVenta` y sus
 * líneas y pagos).
 *
 * Un tipo que no se reconoce **no se sustituye por `Invoice`**. En las líneas y
 * en los pagos el tipo es parte de la clave primaria de la fila y tiene que
 * casar con una cabecera que ya existe: ponerlo mal no deja el documento a
 * medias, lo deja colgando de otro documento o de ninguno. Es el mismo criterio
 * que `TipoDocumentoCierreTurno` tomó del lado de Business Central.
 */
export const mapDocumentTypeForBC = (documentType) => {
  const tipo = tipoDeDocumentoParaBC(documentType);
  if (tipo) return tipo;

  throw new Error(
    `El tipo de documento «${documentType ?? ""}» no es válido: se esperaba Invoice, Boleta o Credit Memo.`
  );
};

// Map el tipo de documento de la UI a el tipo de documento que espera BC
export const UI_TO_SYSTEM_MAP = {
  [COUNTRY_CODES.PERU]: {
    Invoice: "01",
    Boleta: "03",
    "Credit Memo": "07",
  },
};

export const mapUiToSystemType = (value, countryCode) => {
  return UI_TO_SYSTEM_MAP[countryCode]?.[value] || value;
};
